import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { motion } from "framer-motion";

export default function ClimateTimeline() {
  const [years, setYears] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    http.get("/timeline").then(r => setYears(r.data.years || [])).catch(() => {});
  }, []);

  const cur = years[active];

  return (
    <section data-testid="timeline-section" className="relative py-24 md:py-40 px-6 md:px-10 max-w-[1600px] mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
        <div className="md:col-span-5">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">/04 · Timeline</div>
          <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter">
            Thirty-five years<br/>of a warming planet.
          </h2>
        </div>
        <p className="md:col-span-5 md:col-start-7 md:pt-8 text-[color:var(--np-text-2)] text-lg self-end">
          A vertical timeline from 1990 to 2025. Select a year to see how the planet's vitals shifted — and what happened that season.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-3 relative">
          <div className="absolute left-3 top-0 bottom-0 w-px bg-black/10" />
          <ul className="space-y-6">
            {years.map((y, i) => (
              <li key={y.year} className="relative pl-10 cursor-pointer group" onClick={() => setActive(i)} data-testid={`timeline-year-${y.year}`}>
                <span className={`absolute left-0 top-1.5 w-6 h-6 flex items-center justify-center rounded-full border transition-colors ${i === active ? "bg-[color:var(--np-live)] border-[color:var(--np-live)]" : "bg-[color:var(--np-bg)] border-black/25 group-hover:border-white"}`}>
                  {i === active && <span className="w-2 h-2 rounded-full bg-black" />}
                </span>
                <div className={`font-serif-display text-3xl md:text-4xl leading-none transition-colors ${i === active ? "text-white" : "text-[color:var(--np-text-2)] group-hover:text-white"}`}>{y.year}</div>
                <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-[color:var(--np-muted)] mt-1">+{y.temp_anomaly}°C · {y.co2} ppm</div>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-9">
          {cur && (
            <motion.div
              key={cur.year}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10"
            >
              <StatCard label="Year" value={cur.year} unit="" />
              <StatCard label="Temp anomaly" value={`+${cur.temp_anomaly}`} unit="°C vs 1850–1900" accent />
              <StatCard label="Atmospheric CO₂" value={cur.co2} unit="ppm" />
              <StatCard label="Sea level" value={`+${cur.sea_level_mm}`} unit="mm since 1993" />
              <div className="col-span-2 md:col-span-4 bg-[color:var(--np-bg)] p-6 md:p-10">
                <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-muted)] mb-3">What Happened</div>
                <p className="font-serif-display text-2xl md:text-4xl leading-tight tracking-tight">{cur.note}</p>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}

function StatCard({ label, value, unit, accent }) {
  return (
    <div className="bg-[color:var(--np-bg)] p-6">
      <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">{label}</div>
      <div className={`font-serif-display text-4xl md:text-5xl leading-none tracking-tight mt-3 ${accent ? "text-[color:var(--np-alert)]" : "text-[color:var(--np-text)]"}`}>{value}</div>
      <div className="font-mono-tech text-[10px] tracking-wider uppercase mt-2 text-[color:var(--np-text-2)]">{unit}</div>
    </div>
  );
}
